import { FileText, FileSpreadsheet, FileImage, File } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { SensitivityBadge } from './SensitivityBadge';
import { cn } from '@/lib/utils';

interface DocumentCardProps {
  document: {
    id: string;
    title: string;
    file_type: string;
    file_size: number;
    sensitivity: 'normal' | 'confidential';
    processing_status: string;
    created_at: string;
  };
  onClick?: () => void;
  className?: string;
}

function getFileIcon(mime: string) {
  if (mime.includes('spreadsheet') || mime.includes('excel')) return FileSpreadsheet;
  if (mime.includes('image')) return FileImage;
  if (mime.includes('pdf') || mime.includes('word') || mime.includes('text')) return FileText;
  return File;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentCard({ document, onClick, className }: DocumentCardProps) {
  const Icon = getFileIcon(document.file_type);

  return (
    <Card
      className={cn('cursor-pointer transition-colors hover:border-primary/50', className)}
      onClick={onClick}
    >
      <CardContent className="flex flex-col items-center p-6 text-center">
        <Icon className="h-10 w-10 text-muted-foreground" />
        <p className="mt-3 text-sm font-medium line-clamp-2">{document.title}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {formatFileSize(document.file_size)}
        </p>
        <p className="text-xs text-muted-foreground">
          {new Date(document.created_at).toLocaleDateString()}
        </p>
        <div className="mt-2">
          <SensitivityBadge sensitivity={document.sensitivity} />
        </div>
        {document.processing_status === 'processing' && (
          <p className="mt-2 text-xs text-primary animate-pulse">Processing...</p>
        )}
      </CardContent>
    </Card>
  );
}
